import { getLogger } from '@packages/core/logging';

import type { FailCodeSubsectionEntity } from '../entities';

import { StsBackendOperation } from '../../sts-backend-operation';

const logger = getLogger('fail-codes');

/**
 * Parameters for fetching possible fail code subsections filtered by a specific fail code and section.
 *
 * Used when the user selects a section and needs to choose a subsection.
 */
export type FetchSubsectionsParams = {
    /** The fail code to filter subsections by. */
    failCode?: string;

    /** The vehicle model code (e.g. "GUE"). */
    modelCode?: string;

    /** The vehicle model year (e.g. "2024"). */
    modelYear?: string;

    /** The vehicle option/trim code (e.g. "01"). */
    optionCode?: string;

    /** The display name of the parent section. */
    sectionName: string;

    /** The numeric identifier of the parent section. */
    sectionNumber: number;

    /** Optional abort signal for cancellation. */
    signal?: AbortSignal;
};

/**
 * Fetch possible fail code subsections for a vehicle, section, and optional fail code.
 * Used when the user selects a section and needs to choose a subsection.
 */
export class FetchSubsections extends StsBackendOperation {
    /**
     * Fetch possible subsections from the API and return them as domain entities.
     *
     * @param params - the vehicle identifiers, parent section, optional fail code filter, and optional abort signal
     * @returns the matching subsections as domain entities
     */
    async exec(params: FetchSubsectionsParams): Promise<FailCodeSubsectionEntity[]> {
        const { signal, ...request } = params;
        try {
            const response = await this.client.fetchPossibleSubsections(request, signal);

            if (!response.success) {
                return [];
            }

            return response.data.subsections ?? [];
        } catch (e) {
            const message = e instanceof Error ? e.message : 'Failed to get possible sections';
            logger.error('Failed to fetch the fail code subsections', { error: e });
            throw new Error(message);
        }
    }
}
